import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import API from '../utils/api';
import { useAuth } from '../context/AuthContext';
import upiQr from '../images/upi-qr.jpeg';

/* ─── PALETTE ─────────────────────────────────────────────── */
const V1 = '#7F00FF';
const V2 = '#B163FF';
const V3 = '#51158C';
const V4 = '#2B0057';

const PAYMENT_UPI = 'yeeswanthlatha-1@okaxis';

/* ─── SVG ICONS ───────────────────────────────────────────── */
const ClockIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
  </svg>
);
const CreditCardIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="1" y="4" width="22" height="16" rx="2" ry="2" /><line x1="1" y1="10" x2="23" y2="10" />
  </svg>
);
const UploadIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="16 16 12 12 8 16" /><line x1="12" y1="12" x2="12" y2="21" />
    <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
  </svg>
);
const CheckCircleIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);
const RocketIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" />
    <path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z" />
    <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0" />
    <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" />
  </svg>
);

/* ─── CIRCUIT BG ──────────────────────────────────────────── */
const CircuitBg = () => (
  <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden', zIndex: 0, opacity: 0.07 }}>
    <svg viewBox="0 0 1400 800" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice" style={{ width: '100%', height: '100%' }}>
      <g stroke={V2} strokeWidth="0.9" fill="none">
        <line x1="0" y1="160" x2="320" y2="160" /><line x1="380" y1="160" x2="680" y2="160" />
        <line x1="0" y1="420" x2="210" y2="420" /><line x1="570" y1="420" x2="880" y2="420" />
        <circle cx="320" cy="160" r="5" fill={V1} opacity="0.9" />
        <circle cx="680" cy="160" r="4" fill={V2} opacity="0.9" />
        <circle cx="210" cy="420" r="4" fill={V2} opacity="0.9" />
      </g>
    </svg>
  </div>
);

/* ─── FORM FIELD ─────────────────────────────────────────── */
const Field = ({ label, children }) => (
  <div style={{ marginBottom: 20 }}>
    <label style={{
      display: 'block', fontSize: 11.5, color: 'var(--muted)',
      marginBottom: 8, fontWeight: 800,
      letterSpacing: '0.08em', textTransform: 'uppercase',
    }}>{label}</label>
    {children}
  </div>
);

const inputStyle = {
  width: '100%', background: 'var(--bg2)',
  border: '1.5px solid var(--border)', borderRadius: 10,
  color: 'var(--text)', fontFamily: "'Satoshi','DM Sans',sans-serif",
  fontSize: 15, padding: '12px 16px', outline: 'none',
  transition: 'border-color 0.2s, box-shadow 0.2s',
};

const Apply = () => {
  const { courseId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    studentPhone: '', qualification: '', degree: '', degreeOther: '', collegeName: '', message: '',
    gpayTransactionId: '', paymentScreenshot: null,
  });

  useEffect(() => {
    API.get(`/courses/${courseId}`).then(res => setCourse(res.data)).catch(() => navigate('/courses'));
  }, [courseId, navigate]);

  const handleChange = e => {
    const { name, value, files } = e.target;
    setForm(prev => ({ ...prev, [name]: files ? files[0] : value }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.paymentScreenshot) { toast.error('Please upload your UPI payment screenshot'); return; }
    if (!form.gpayTransactionId) { toast.error('Please enter your UPI Transaction ID'); return; }

    setLoading(true);
    const data = new FormData();
    data.append('courseId', courseId);
    data.append('studentPhone', form.studentPhone);
    data.append('qualification', form.degree === 'Other' ? form.degreeOther : form.degree);
    data.append('degree', form.degree === 'Other' ? form.degreeOther : form.degree);
    data.append('collegeName', form.collegeName);
    data.append('message', form.message);
    data.append('gpayTransactionId', form.gpayTransactionId);
    data.append('amountPaid', course.fee);
    data.append('paymentScreenshot', form.paymentScreenshot);

    try {
      await API.post('/applications', data, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Application submitted successfully!');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  if (!course) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: 'var(--bg)', color: 'var(--muted)', flexDirection: 'column', gap: 16 }}>
      <div style={{ width: 36, height: 36, borderRadius: '50%', border: `3px solid ${V1}33`, borderTopColor: V1, animation: 'spin 0.8s linear infinite' }} />
      Loading...
    </div>
  );

  return (
    <div style={{ background: 'var(--bg)', color: 'var(--text)', minHeight: '100vh' }}>

      {/* ══ HEADER ══════════════════════════════════════════ */}
      <section style={{
        padding: '120px 0 60px', position: 'relative', overflow: 'hidden',
        background: 'var(--bg)',
      }}>
        <CircuitBg />
        <div style={{ position: 'absolute', bottom: 0, right: 0, width: 360, height: 360, borderRadius: '50%', background: `radial-gradient(circle, var(--hero-orb) 0%, transparent 70%)`, pointerEvents: 'none' }} />

        <div className="container" style={{ position: 'relative', zIndex: 2, maxWidth: 860 }}>
          <div className="section-label">COURSE APPLICATION</div>
          <h1 style={{
            fontSize: 'clamp(28px,4vw,48px)', fontWeight: 800, letterSpacing: '-0.025em', marginBottom: 12,
            fontFamily: "'Inter',sans-serif",
          }}>
            Apply for <span style={{
              background: `linear-gradient(135deg,${V1},${V2})`,
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
            }}>Course</span>
          </h1>
          <p style={{ color: 'var(--muted)', fontSize: 16, lineHeight: 1.6 }}>
            Complete the form below to enroll in{' '}
            <strong style={{ color: 'var(--text)', fontWeight: 800 }}>{course.title}</strong>
          </p>
        </div>
      </section>

      {/* ══ FORM AREA ═══════════════════════════════════════ */}
      <section style={{ padding: '0 0 100px', background: 'var(--bg2)', borderTop: '1px solid var(--border)' }}>
        <div className="container" style={{ maxWidth: 860, paddingTop: 48 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 32, alignItems: 'start' }} className="apply-grid">

            {/* LEFT: Main form */}
            <div>
              {/* Payment Instructions */}
              <div style={{
                marginBottom: 28,
                background: 'var(--card-solid)', border: `1px solid ${V1}44`,
                borderRadius: 20, overflow: 'hidden',
              }}>
                <div style={{
                  padding: '18px 24px',
                  background: `linear-gradient(135deg, var(--badge-pill-bg), var(--bg3))`,
                  borderBottom: `1px solid var(--border)`,
                  display: 'flex', alignItems: 'center', gap: 10,
                }}>
                  <span style={{ color: V2 }}><CreditCardIcon /></span>
                  <h3 style={{ fontSize: 16, fontWeight: 800, color: 'var(--text)', fontFamily: "'Inter',sans-serif" }}>Payment Instructions</h3>
                </div>
                <ol style={{ color: 'var(--text-2)', lineHeight: 2.1, padding: '20px 24px 20px 44px', fontSize: 14.5 }}>
                  <li>Open <strong style={{ color: 'var(--text)' }}>Google Pay</strong> on your phone</li>
                  <li>Send <strong style={{ color: V2 }}>₹{course.fee.toLocaleString()}</strong> to UPI ID:{' '}
                    <code style={{ background: 'var(--badge-pill-bg)', border: '1px solid var(--badge-pill-border)', color: 'var(--badge-pill-text)', padding: '2px 10px', borderRadius: 6, fontSize: 13 }}>{PAYMENT_UPI}</code>
                  </li>
                  <li>After payment, <strong style={{ color: 'var(--text)' }}>take a screenshot</strong> of the confirmation</li>
                  <li>Enter the <strong style={{ color: 'var(--text)' }}>Transaction ID</strong> and upload the screenshot below</li>
                </ol>
              </div>

              {/* Application Form */}
              <form onSubmit={handleSubmit} style={{
                background: 'var(--card-solid)', border: '1px solid var(--border)',
                borderRadius: 20, padding: '32px',
                boxShadow: 'var(--shadow-card)',
              }}>
                <h3 style={{ fontSize: 17, fontWeight: 800, marginBottom: 24, letterSpacing: '-0.01em', fontFamily: "'Inter',sans-serif" }}>Student Details</h3>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <Field label="Full Name">
                    <input value={user.name} disabled style={{ ...inputStyle, opacity: 0.55, cursor: 'not-allowed' }} />
                  </Field>
                  <Field label="Email">
                    <input value={user.email} disabled style={{ ...inputStyle, opacity: 0.55, cursor: 'not-allowed' }} />
                  </Field>
                </div>

                <Field label="Phone Number *">
                  <input
                    name="studentPhone" value={form.studentPhone} onChange={handleChange}
                    placeholder="Enter your mobile number" required
                    style={inputStyle}
                    onFocus={e => { e.target.style.borderColor = `${V1}88`; e.target.style.boxShadow = `0 0 0 3px ${V1}22`; }}
                    onBlur={e => { e.target.style.borderColor = 'var(--border)'; e.target.style.boxShadow = 'none'; }}
                  />
                </Field>

                <Field label="Degree / Qualification *">
                  {/* <select
                    name="degree" value={form.degree} onChange={handleChange} required
                    style={{ ...inputStyle, appearance: 'none', WebkitAppearance: 'none' }}
                    onFocus={e => { e.target.style.borderColor = `${V1}88`; e.target.style.boxShadow = `0 0 0 3px ${V1}22`; }}
                    onBlur={e => { e.target.style.borderColor = 'var(--border)'; e.target.style.boxShadow = 'none'; }}
                  >
                    <option value="">Select your degree / qualification</option>
                    <optgroup label="School">
                      <option>10th Grade (SSLC)</option>
                      <option>11th Grade</option>
                      <option>12th Grade (HSC)</option>
                    </optgroup>
                    <optgroup label="Undergraduate">
                      <option>B.E. / B.Tech</option>
                      <option>B.Sc</option>
                      <option>B.Com</option>
                      <option>B.A.</option>
                      <option>B.B.A.</option>
                      <option>B.C.A.</option>
                      <option>Diploma</option>
                    </optgroup>
                    <optgroup label="Postgraduate">
                      <option>M.E. / M.Tech</option>
                      <option>M.Sc</option>
                      <option>M.Com</option>
                      <option>M.A.</option>
                      <option>M.B.A.</option>
                      <option>M.C.A.</option>
                    </optgroup>
                    <option value="Other">Other (type manually)</option>
                  </select> */}
                  <select
                    name="degree" value={form.degree} onChange={handleChange} required
                    style={{ ...inputStyle, appearance: 'none', WebkitAppearance: 'none' }}
                    onFocus={e => { e.target.style.borderColor = `${V1}88`; e.target.style.boxShadow = `0 0 0 3px ${V1}22`; }}
                    onBlur={e => { e.target.style.borderColor = 'var(--border)'; e.target.style.boxShadow = 'none'; }}
                  >
                    <option value="">Select your degree / qualification</option>

                    <optgroup label="🏫 School">
                      <option>10th Grade (SSLC)</option>
                      <option>11th Grade</option>
                      <option>12th Grade (HSC) - Science</option>
                      <option>12th Grade (HSC) - Commerce</option>
                      <option>12th Grade (HSC) - Arts</option>
                    </optgroup>

                    <optgroup label="📜 Diploma">
                      <option>Diploma in Engineering</option>
                      <option>Diploma in Computer Science</option>
                      <option>Diploma in Mechanical Engineering</option>
                      <option>Diploma in Civil Engineering</option>
                      <option>Diploma in Electrical Engineering</option>
                      <option>Diploma in Electronics & Communication</option>
                      <option>Diploma in Information Technology</option>
                      <option>Diploma in Business Administration</option>
                      <option>Diploma in Hotel Management</option>
                      <option>Diploma in Pharmacy</option>
                    </optgroup>

                    <optgroup label="🎓 B.E. / B.Tech">
                      <option>B.E. / B.Tech - Computer Science Engineering</option>
                      <option>B.E. / B.Tech - Information Technology</option>
                      <option>B.E. / B.Tech - Mechanical Engineering</option>
                      <option>B.E. / B.Tech - Civil Engineering</option>
                      <option>B.E. / B.Tech - Electrical Engineering</option>
                      <option>B.E. / B.Tech - Electronics & Communication</option>
                      <option>B.E. / B.Tech - Electrical & Electronics</option>
                      <option>B.E. / B.Tech - Aeronautical Engineering</option>
                      <option>B.E. / B.Tech - Automobile Engineering</option>
                      <option>B.E. / B.Tech - Biomedical Engineering</option>
                      <option>B.E. / B.Tech - Biotechnology</option>
                      <option>B.E. / B.Tech - Chemical Engineering</option>
                      <option>B.E. / B.Tech - Artificial Intelligence & ML</option>
                      <option>B.E. / B.Tech - Data Science</option>
                      <option>B.E. / B.Tech - Cyber Security</option>
                      <option>B.E. / B.Tech - Robotics & Automation</option>
                    </optgroup>

                    <optgroup label="🎓 B.Sc">
                      <option>B.Sc - Computer Science</option>
                      <option>B.Sc - Information Technology</option>
                      <option>B.Sc - Software Engineering</option>
                      <option>B.Sc - Data Science</option>
                      <option>B.Sc - Artificial Intelligence</option>
                      <option>B.Sc - Mathematics</option>
                      <option>B.Sc - Physics</option>
                      <option>B.Sc - Chemistry</option>
                      <option>B.Sc - Biology</option>
                      <option>B.Sc - Biotechnology</option>
                      <option>B.Sc - Nursing</option>
                      <option>B.Sc - Agriculture</option>
                      <option>B.Sc - Nutrition & Dietetics</option>
                      <option>B.Sc - Psychology</option>
                      <option>B.Sc - Statistics</option>
                    </optgroup>

                    <optgroup label="🎓 B.Com">
                      <option>B.Com - General</option>
                      <option>B.Com - Computer Applications</option>
                      <option>B.Com - Accounting & Finance</option>
                      <option>B.Com - Banking & Insurance</option>
                      <option>B.Com - Professional Accounting</option>
                      <option>B.Com - E-Commerce</option>
                      <option>B.Com - Taxation</option>
                    </optgroup>

                    <optgroup label="🎓 B.A.">
                      <option>B.A. - English</option>
                      <option>B.A. - Tamil</option>
                      <option>B.A. - History</option>
                      <option>B.A. - Economics</option>
                      <option>B.A. - Political Science</option>
                      <option>B.A. - Sociology</option>
                      <option>B.A. - Psychology</option>
                      <option>B.A. - Geography</option>
                      <option>B.A. - Journalism & Mass Communication</option>
                      <option>B.A. - Public Administration</option>
                    </optgroup>

                    <optgroup label="🎓 B.B.A.">
                      <option>B.B.A. - General</option>
                      <option>B.B.A. - Computer Applications</option>
                      <option>B.B.A. - International Business</option>
                      <option>B.B.A. - Logistics & Supply Chain</option>
                      <option>B.B.A. - Entrepreneurship</option>
                      <option>B.B.A. - Finance</option>
                      <option>B.B.A. - Marketing</option>
                      <option>B.B.A. - Human Resource Management</option>
                    </optgroup>

                    <optgroup label="🎓 B.C.A.">
                      <option>B.C.A. - General</option>
                      <option>B.C.A. - Data Science</option>
                      <option>B.C.A. - Cloud Computing</option>
                      <option>B.C.A. - Cyber Security</option>
                      <option>B.C.A. - AI & Machine Learning</option>
                    </optgroup>

                    <optgroup label="🎓 Other UG">
                      <option>B.Arch - Architecture</option>
                      <option>B.Pharm - Pharmacy</option>
                      <option>B.Ed - Education</option>
                      <option>B.L. / LLB - Law</option>
                      <option>B.F.A. - Fine Arts</option>
                      <option>B.Des - Design</option>
                      <option>B.H.M. - Hotel Management</option>
                      <option>B.P.T. - Physiotherapy</option>
                      <option>MBBS - Medicine</option>
                      <option>BDS - Dental Surgery</option>
                      <option>B.V.Sc - Veterinary Science</option>
                    </optgroup>

                    <optgroup label="🎓 M.E. / M.Tech">
                      <option>M.E. / M.Tech - Computer Science Engineering</option>
                      <option>M.E. / M.Tech - Information Technology</option>
                      <option>M.E. / M.Tech - Mechanical Engineering</option>
                      <option>M.E. / M.Tech - Civil Engineering</option>
                      <option>M.E. / M.Tech - Electrical Engineering</option>
                      <option>M.E. / M.Tech - Electronics & Communication</option>
                      <option>M.E. / M.Tech - VLSI Design</option>
                      <option>M.E. / M.Tech - Embedded Systems</option>
                      <option>M.E. / M.Tech - Structural Engineering</option>
                      <option>M.E. / M.Tech - Artificial Intelligence</option>
                      <option>M.E. / M.Tech - Data Science</option>
                      <option>M.E. / M.Tech - Cyber Security</option>
                      <option>M.E. / M.Tech - Robotics</option>
                    </optgroup>

                    <optgroup label="🎓 M.Sc">
                      <option>M.Sc - Computer Science</option>
                      <option>M.Sc - Information Technology</option>
                      <option>M.Sc - Software Engineering</option>
                      <option>M.Sc - Data Science</option>
                      <option>M.Sc - Artificial Intelligence</option>
                      <option>M.Sc - Mathematics</option>
                      <option>M.Sc - Physics</option>
                      <option>M.Sc - Chemistry</option>
                      <option>M.Sc - Biotechnology</option>
                      <option>M.Sc - Statistics</option>
                      <option>M.Sc - Psychology</option>
                    </optgroup>

                    <optgroup label="🎓 M.Com">
                      <option>M.Com - General</option>
                      <option>M.Com - Accounting & Finance</option>
                      <option>M.Com - Banking & Insurance</option>
                      <option>M.Com - E-Commerce</option>
                      <option>M.Com - Taxation</option>
                    </optgroup>

                    <optgroup label="🎓 M.A.">
                      <option>M.A. - English</option>
                      <option>M.A. - Tamil</option>
                      <option>M.A. - History</option>
                      <option>M.A. - Economics</option>
                      <option>M.A. - Political Science</option>
                      <option>M.A. - Sociology</option>
                      <option>M.A. - Psychology</option>
                      <option>M.A. - Journalism & Mass Communication</option>
                      <option>M.A. - Public Administration</option>
                    </optgroup>

                    <optgroup label="🎓 M.B.A.">
                      <option>M.B.A. - General Management</option>
                      <option>M.B.A. - Finance</option>
                      <option>M.B.A. - Marketing</option>
                      <option>M.B.A. - Human Resource Management</option>
                      <option>M.B.A. - Operations Management</option>
                      <option>M.B.A. - International Business</option>
                      <option>M.B.A. - Business Analytics</option>
                      <option>M.B.A. - Entrepreneurship</option>
                      <option>M.B.A. - Information Technology</option>
                      <option>M.B.A. - Supply Chain Management</option>
                      <option>M.B.A. - Hospital Management</option>
                    </optgroup>

                    <optgroup label="🎓 M.C.A.">
                      <option>M.C.A. - General</option>
                      <option>M.C.A. - Data Science</option>
                      <option>M.C.A. - Cloud Computing</option>
                      <option>M.C.A. - Cyber Security</option>
                      <option>M.C.A. - AI & Machine Learning</option>
                    </optgroup>

                    <optgroup label="🎓 Other PG">
                      <option>M.Pharm - Pharmacy</option>
                      <option>M.Ed - Education</option>
                      <option>LLM - Law</option>
                      <option>M.Arch - Architecture</option>
                      <option>M.Des - Design</option>
                      <option>M.Phil - Research</option>
                      <option>Ph.D - Doctorate</option>
                      <option>MD - Medicine</option>
                      <option>MS - Surgery</option>
                    </optgroup>

                    <option value="Other">Other (type manually)</option>
                  </select>
                </Field>

                {form.degree === 'Other' && (
                  <Field label="Specify your Qualification *">
                    <input
                      name="degreeOther" value={form.degreeOther} onChange={handleChange}
                      placeholder="e.g. ITI, Polytechnic, PhD..."
                      required
                      style={inputStyle}
                      onFocus={e => { e.target.style.borderColor = `${V1}88`; e.target.style.boxShadow = `0 0 0 3px ${V1}22`; }}
                      onBlur={e => { e.target.style.borderColor = 'var(--border)'; e.target.style.boxShadow = 'none'; }}
                    />
                  </Field>
                )}

                <Field label="College / School Name *">
                  <input
                    name="collegeName" value={form.collegeName} onChange={handleChange}
                    placeholder="e.g. Anna University, Sri Venkateswara College..."
                    required
                    style={inputStyle}
                    onFocus={e => { e.target.style.borderColor = `${V1}88`; e.target.style.boxShadow = `0 0 0 3px ${V1}22`; }}
                    onBlur={e => { e.target.style.borderColor = 'var(--border)'; e.target.style.boxShadow = 'none'; }}
                  />
                </Field>

                <Field label="Why do you want to join this course?">
                  <textarea
                    name="message" value={form.message} onChange={handleChange}
                    placeholder="Tell us a bit about yourself and your goals..."
                    style={{ ...inputStyle, resize: 'vertical', minHeight: 100 }}
                    onFocus={e => { e.target.style.borderColor = `${V1}88`; e.target.style.boxShadow = `0 0 0 3px ${V1}22`; }}
                    onBlur={e => { e.target.style.borderColor = 'var(--border)'; e.target.style.boxShadow = 'none'; }}
                  />
                </Field>

                <div style={{ height: 1, background: 'var(--border)', margin: '24px 0' }} />

                <h3 style={{ fontSize: 17, fontWeight: 800, marginBottom: 24, letterSpacing: '-0.01em', fontFamily: "'Inter',sans-serif" }}>Payment Details</h3>

                <Field label="UPI Transaction ID / UTR *">
                  <input
                    name="gpayTransactionId" value={form.gpayTransactionId} onChange={handleChange}
                    placeholder="e.g. 316792834123 (from payment confirmation)" required
                    style={inputStyle}
                    onFocus={e => { e.target.style.borderColor = `${V1}88`; e.target.style.boxShadow = `0 0 0 3px ${V1}22`; }}
                    onBlur={e => { e.target.style.borderColor = 'var(--border)'; e.target.style.boxShadow = 'none'; }}
                  />
                </Field>

                <Field label="Payment Screenshot *">
                  <div
                    style={{
                      border: `2px dashed ${form.paymentScreenshot ? V1 : 'var(--border-strong)'}`,
                      borderRadius: 12, padding: '32px 24px', textAlign: 'center', cursor: 'pointer',
                      background: form.paymentScreenshot ? `${V4}22` : 'var(--bg2)',
                      transition: 'all 0.2s',
                    }}
                    onClick={() => document.getElementById('screenshotInput').click()}
                    onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = V1; e.currentTarget.style.background = `${V4}33`; }}
                    onDragLeave={e => { e.currentTarget.style.borderColor = form.paymentScreenshot ? V1 : 'var(--border-strong)'; e.currentTarget.style.background = form.paymentScreenshot ? `${V4}22` : 'var(--bg2)'; }}
                    onDrop={e => {
                      e.preventDefault();
                      const file = e.dataTransfer.files[0];
                      if (file) setForm(p => ({ ...p, paymentScreenshot: file }));
                    }}
                  >
                    <input id="screenshotInput" type="file" name="paymentScreenshot" accept="image/*" onChange={handleChange} style={{ display: 'none' }} />
                    {form.paymentScreenshot ? (
                      <div>
                        <div style={{ color: 'var(--success)', display: 'flex', justifyContent: 'center', marginBottom: 8 }}>
                          <CheckCircleIcon />
                        </div>
                        <div style={{ color: 'var(--success)', fontWeight: 700, fontSize: 15 }}>File selected</div>
                        <div style={{ color: 'var(--muted)', fontSize: 13, marginTop: 4 }}>{form.paymentScreenshot.name}</div>
                      </div>
                    ) : (
                      <div>
                        <div style={{ color: 'var(--muted)', display: 'flex', justifyContent: 'center', marginBottom: 10 }}><UploadIcon /></div>
                        <div style={{ color: 'var(--text-2)', fontSize: 14, fontWeight: 600 }}>Click or drag your payment screenshot here</div>
                        <div style={{ color: 'var(--muted)', fontSize: 12, marginTop: 4 }}>JPG, PNG — Max 5MB</div>
                      </div>
                    )}
                  </div>
                </Field>

                <button
                  type="submit" disabled={loading}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                    padding: '15px', borderRadius: 12, border: 'none', cursor: loading ? 'not-allowed' : 'pointer',
                    background: `linear-gradient(135deg,${V1},${V3})`,
                    color: '#fff', fontWeight: 800, fontSize: 16,
                    fontFamily: "'Satoshi','DM Sans',sans-serif",
                    boxShadow: loading ? 'none' : `0 6px 24px ${V1}44`,
                    opacity: loading ? 0.7 : 1,
                    transition: 'all 0.25s cubic-bezier(0.34,1.56,0.64,1)',
                    marginTop: 8,
                  }}
                  onMouseEnter={e => { if (!loading) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = `0 14px 36px ${V1}55`; } }}
                  onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = loading ? 'none' : `0 6px 24px ${V1}44`; }}
                >
                  {loading ? (
                    <>
                      <div style={{ width: 18, height: 18, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', animation: 'spin 0.7s linear infinite' }} />
                      Submitting...
                    </>
                  ) : (
                    <><RocketIcon /> Submit Application</>
                  )}
                </button>
              </form>
            </div>

            {/* RIGHT: Course summary + QR card */}
            <div style={{ position: 'sticky', top: 100 }}>
              {/* Course summary */}
              <div style={{
                background: 'var(--card-solid)', border: `1px solid ${V1}44`,
                borderRadius: 20, overflow: 'hidden',
                boxShadow: 'var(--shadow-md)', marginBottom: 20,
              }}>
                <div style={{ height: 4, background: `linear-gradient(90deg,${V1},${V2})` }} />
                <div style={{ padding: '24px' }}>
                  <div style={{ fontSize: 11, color: V2, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>Enrolling In</div>
                  <h3 style={{ fontSize: 16, fontWeight: 800, marginBottom: 12, letterSpacing: '-0.01em', fontFamily: "'Inter',sans-serif" }}>{course.title}</h3>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--muted)', fontSize: 13, marginBottom: 20 }}>
                    <span style={{ color: V2 }}><ClockIcon /></span>{course.duration} &nbsp;·&nbsp; {course.level}
                  </div>

                  <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ color: 'var(--muted)', fontSize: 13 }}>Course Fee</span>
                      <span style={{
                        fontFamily: "'Inter',sans-serif", fontWeight: 900,
                        fontSize: 28, color: 'var(--text)',
                      }}>₹{course.fee.toLocaleString()}</span>
                    </div>
                    <div style={{ color: 'var(--muted)', fontSize: 11.5, textAlign: 'right', marginTop: 2 }}>One-time via UPI</div>
                  </div>
                </div>
              </div>

              {/* UPI QR Code card */}
              <div style={{
                background: 'var(--card-solid)', border: `1px solid ${V1}44`,
                borderRadius: 20, overflow: 'hidden',
                boxShadow: 'var(--shadow-md)',
              }}>
                <div style={{
                  padding: '16px 24px',
                  background: `linear-gradient(135deg, ${V4}, ${V3})`,
                  display: 'flex', alignItems: 'center', gap: 10,
                }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="3" y="14" width="7" height="7" />
                    <rect x="14" y="14" width="3" height="3" /><rect x="18" y="14" width="3" height="3" /><rect x="14" y="18" width="3" height="3" /><rect x="18" y="18" width="3" height="3" />
                  </svg>
                  <span style={{ color: '#fff', fontSize: 14, fontWeight: 800, fontFamily: "'Inter',sans-serif" }}>Scan & Pay</span>
                </div>
                <div style={{ padding: '20px 24px', textAlign: 'center' }}>
                  <div style={{
                    background: '#fff', borderRadius: 16, padding: 16, marginBottom: 16,
                    display: 'inline-block',
                    boxShadow: `0 4px 20px rgba(0,0,0,0.08)`,
                  }}>
                    <img
                      src={upiQr}
                      alt="UPI QR Code"
                      style={{ width: '100%', maxWidth: 200, height: 'auto', display: 'block', borderRadius: 8 }}
                    />
                  </div>
                  <p style={{ color: 'var(--text)', fontSize: 14, fontWeight: 700, marginBottom: 6 }}>
                    Scan with any UPI app
                  </p>
                  <p style={{ color: 'var(--muted)', fontSize: 12, lineHeight: 1.6 }}>
                    Google Pay · PhonePe · Paytm
                  </p>
                  <div style={{
                    marginTop: 14, padding: '10px 16px',
                    background: 'var(--bg3)', borderRadius: 10,
                    border: '1px solid var(--border)',
                  }}>
                    <div style={{ fontSize: 10, color: 'var(--muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>Amount</div>
                    <div style={{ fontFamily: "'Inter',sans-serif", fontWeight: 900, fontSize: 22, color: V2 }}>₹{course.fee.toLocaleString()}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @media (max-width: 860px) {
          .apply-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
};

export default Apply;
